import { useCallback, useEffect, useRef, useState } from 'react'

export interface PhotoGridNavigation {
  /** Attach to the grid container; the hook reads tile geometry through it. */
  gridRef: React.MutableRefObject<HTMLDivElement | null>
  /** The one tile in the grid carrying tabIndex=0 (roving tabindex). */
  focusedIndex: number
  /** Keeps the tab stop under the tile a pointer just used. */
  setFocusedIndex: (index: number) => void
  onKeyDown: (event: React.KeyboardEvent) => void
}

const TILE = '[data-photo-selectable]'
const indexOf = (el: Element) => Number(el.getAttribute('data-photo-index'))

/**
 * Roving-tabindex keyboard navigation for the owner photo grid (SOL-82).
 *
 * The grid is ONE tab stop, not one per photo: Tab reaches it, Tab again
 * leaves it, and arrows move within. Three hundred tab stops would make the
 * rest of the page unreachable in practice, which is the failure this shape
 * exists to avoid.
 *
 * Movement NEVER changes the selection (Shift+Arrow aside, which extends
 * deliberately). That mirrors the mouse: a click selects, an arrow only
 * looks. The alternative -- select-as-you-move -- silently destroys a
 * multi-selection the moment someone presses an arrow to check what they
 * picked.
 */
export function usePhotoGridNavigation({
  itemCount,
  onToggle,
  onExtend,
}: {
  itemCount: number
  /** Space/Enter: toggle the focused photo. */
  onToggle: (index: number) => void
  /** Shift+Arrow: extend the range to the newly focused photo. */
  onExtend: (index: number) => void
}): PhotoGridNavigation {
  const gridRef = useRef<HTMLDivElement | null>(null)
  const [focusedIndex, setFocusedIndex] = useState(0)

  // Photos can be deleted while the grid has focus. An index past the end
  // would leave NO tile carrying tabIndex=0, which makes the whole grid
  // unreachable by Tab -- worse than the state it came from.
  useEffect(() => {
    setFocusedIndex((current) => (current > itemCount - 1 ? Math.max(0, itemCount - 1) : current))
  }, [itemCount])

  // Only selectable tiles: an in-flight upload has no file id to select and
  // nothing to focus, so navigation steps over it rather than stopping dead
  // on a tile that ignores every key.
  const tiles = useCallback(
    () => Array.from(gridRef.current?.querySelectorAll(TILE) ?? []),
    [],
  )

  const focus = useCallback((index: number) => {
    setFocusedIndex(index)
    const el = tiles().find((t) => indexOf(t) === index)
    if (el instanceof HTMLElement) el.focus()
  }, [tiles])

  /**
   * The tile one row up or down, by GEOMETRY.
   *
   * The grid is `repeat(auto-fill, minmax(...))`, so the column count is
   * decided by the browser from the viewport and the current view mode.
   * Nothing tells JS what it is, and a hardcoded number would be wrong at
   * every breakpoint. Rows are therefore grouped by measured `top`, and the
   * target is the tile nearest the current column -- which also answers the
   * ragged last row, where the column directly below may not exist.
   */
  const verticalTarget = (from: number, direction: -1 | 1): number | null => {
    const all = tiles()
    const current = all.find((t) => indexOf(t) === from)
    if (!current) return null

    const rects = new Map(all.map((t) => [t, t.getBoundingClientRect()]))
    const currentRect = rects.get(current)!
    const rows = [...new Set(all.map((t) => rects.get(t)!.top))].sort((a, b) => a - b)
    const targetTop = rows[rows.indexOf(currentRect.top) + direction]
    if (targetTop === undefined) return null

    const row = all.filter((t) => rects.get(t)!.top === targetTop)
    const nearest = row.reduce((best, t) =>
      Math.abs(rects.get(t)!.left - currentRect.left) < Math.abs(rects.get(best)!.left - currentRect.left)
        ? t
        : best)
    return indexOf(nearest)
  }

  const horizontalTarget = (from: number, direction: -1 | 1): number | null => {
    const order = tiles().map(indexOf)
    const at = order.indexOf(from)
    if (at === -1) return null
    // Clamped, not wrapped: arrowing off the last photo and landing on the
    // first reads as a glitch rather than a feature.
    return order[at + direction] ?? null
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    const order = tiles().map(indexOf)
    let target: number | null = null

    switch (event.key) {
      case 'ArrowRight': target = horizontalTarget(focusedIndex, 1); break
      case 'ArrowLeft': target = horizontalTarget(focusedIndex, -1); break
      case 'ArrowDown': target = verticalTarget(focusedIndex, 1); break
      case 'ArrowUp': target = verticalTarget(focusedIndex, -1); break
      case 'Home': target = order[0] ?? null; break
      case 'End': target = order[order.length - 1] ?? null; break
      case ' ':
      case 'Enter':
        // Space would scroll the grid out from under the photo it just
        // selected; Enter has no default here but is prevented alongside it
        // so both keys behave identically.
        event.preventDefault()
        onToggle(focusedIndex)
        return
      default:
        // Every other key belongs to the page -- no preventDefault, so typing
        // and browser shortcuts still work with the grid focused.
        return
    }

    event.preventDefault()
    // A move that hits an edge still counts as handled: the default action
    // (scrolling the page) is exactly what should not happen.
    if (target === null || target === focusedIndex) return

    focus(target)
    if (event.shiftKey) onExtend(target)
  }

  return { gridRef, focusedIndex, setFocusedIndex, onKeyDown }
}
